
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow
} from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EditItemDialog } from "@/components/inventory/EditItemDialog";
import { DeleteItemDialog } from "@/components/inventory/DeleteItemDialog";
import { MoreHorizontal, Search } from "lucide-react";
import { useInventoryStore, InventoryItem } from "@/store/api/inventoryStore";
import { useCurrency } from "@/contexts/CurrencyContext";
import { formatDate } from "@/utils/dateUtils";
import { useIsMobile } from "@/hooks/use-mobile";

export function InventoryTable() {
  const { t } = useTranslation(["inventory", "common"]);
  const { items, isLoading } = useInventoryStore();
  const { formatCurrency } = useCurrency();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Filter items based on search and category
  const filteredItems = items.filter(item => {
    const matchesSearch = search === "" || 
      item.name.toLowerCase().includes(search.toLowerCase()) || 
      item.size.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-2 sm:space-y-4">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
        <div className={`relative w-full sm:w-72 ${isMobile ? "h-7" : ""}`}>
          <Search className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${isMobile ? "h-3 w-3" : "h-4 w-4"} text-muted-foreground`} />
          <Input
            placeholder={t('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${isMobile ? "pl-7 h-7 text-xs" : "pl-8"}`}
          />
        </div>
        <Select
          value={categoryFilter}
          onValueChange={setCategoryFilter}
        >
          <SelectTrigger className={`w-full sm:w-36 ${isMobile ? "h-7 text-xs" : ""}`}>
            <SelectValue placeholder={t('filterByCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allItems')}</SelectItem>
            <SelectItem value="suits">{t('suits')}</SelectItem>
            <SelectItem value="equipment">{t('equipment')}</SelectItem>
            <SelectItem value="accessories">{t('accessories')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isMobile ? "px-1 py-1 text-[8px]" : ""}>{t('item')}</TableHead>
              <TableHead className={isMobile ? "px-1 py-1 text-[8px]" : ""}>{t('size')}</TableHead>
              <TableHead className={`text-center ${isMobile ? "px-1 py-1 text-[8px]" : ""}`}>{t('stock')}</TableHead>
              <TableHead className={`text-center ${isMobile ? "px-1 py-1 text-[8px] hidden" : ""}`}>{t('available')}</TableHead>
              <TableHead className={`text-center ${isMobile ? "px-1 py-1 text-[8px] hidden" : ""}`}>{t('reserved')}</TableHead>
              <TableHead className={`text-right ${isMobile ? "px-1 py-1 text-[8px]" : ""}`}>{t('price')}</TableHead>
              <TableHead className={`text-center ${isMobile ? "px-1 py-1 text-[8px] hidden" : ""}`}>{t('lastUpdated')}</TableHead>
              <TableHead className={`text-right ${isMobile ? "px-1 py-1 text-[8px]" : ""}`}>{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className={`h-24 text-center ${isMobile ? "text-[8px]" : ""}`}>
                  {t('loading', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className={`h-24 text-center ${isMobile ? "text-[8px]" : ""}`}>
                  {t('noItemsFound')}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className={isMobile ? "px-1 py-0.5" : ""}>
                    <div className={`font-medium ${isMobile ? "text-[8px]" : ""}`}>
                      {isMobile && item.name.length > 15 
                        ? `${item.name.substring(0, 12)}...` 
                        : item.name}
                    </div>
                    <div className={`${isMobile ? "text-[8px] mt-0" : "text-xs"} text-muted-foreground`}>
                      <Badge variant="outline" size={isMobile ? "xs" : "default"}>
                        {t(`${item.category}`)}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className={`${isMobile ? "px-1 py-0.5 text-[8px]" : ""}`}>
                    {item.size}
                  </TableCell>
                  <TableCell className={`text-center ${isMobile ? "px-1 py-0.5 text-[8px]" : ""}`}>
                    {item.stock}
                  </TableCell>
                  <TableCell className={`text-center ${isMobile ? "px-1 py-0.5 text-[8px] hidden" : ""}`}>
                    {item.available}
                  </TableCell>
                  <TableCell className={`text-center ${isMobile ? "px-1 py-0.5 text-[8px] hidden" : ""}`}>
                    {item.reserved}
                  </TableCell>
                  <TableCell className={`text-right ${isMobile ? "px-1 py-0.5 text-[8px]" : ""}`}>
                    {formatCurrency(item.price)}
                  </TableCell>
                  <TableCell className={`text-center ${isMobile ? "px-1 py-0.5 text-[8px] hidden" : ""}`}>
                    {formatDate(new Date(item.lastUpdated), 'PP')}
                  </TableCell>
                  <TableCell className={`text-right ${isMobile ? "px-1 py-0.5" : ""}`}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size={isMobile ? "icon-xs" : "sm"} className={isMobile ? "h-5 w-5 p-0" : "h-8 w-8 p-0"}>
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className={isMobile ? "h-2.5 w-2.5" : "h-4 w-4"} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className={isMobile ? "text-[8px]" : ""}>
                        <DropdownMenuLabel className={isMobile ? "py-1 text-[8px]" : ""}>
                          {t('actions')}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => setEditingItem(item)}
                          className={isMobile ? "py-1 text-[8px]" : ""}
                        >
                          {t('edit', { ns: 'common' })}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeletingItemId(item.id)}
                          className={isMobile ? "py-1 text-[8px]" : ""}
                        >
                          {t('delete', { ns: 'common' })}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editingItem && (
        <EditItemDialog 
          item={editingItem} 
          open={!!editingItem} 
          onOpenChange={(open) => !open && setEditingItem(null)}
        />
      )}

      <DeleteItemDialog 
        itemId={deletingItemId} 
        open={!!deletingItemId} 
        onOpenChange={(open) => !open && setDeletingItemId(null)}
      />
    </div>
  );
}
